<svelte:options
  customElement={{
    tag: "oc-focused-dialog-v1",

    extend: window.__components.extend(),
    props: {
      inApp: { type: "Boolean", reflect: true },
      headline: { type: "String", reflect: true },
      headlineLevel: { type: "Number", attribute: "headline-level" },
      closeUrl: { type: "String", attribute: "close-url", reflect: true },
      prevUrl: { type: "String", attribute: "prev-url", reflect: true },
      showBackButton: { type: "Boolean", attribute: "show-back-button", reflect: true },
    },
  }}
/>

<script lang="ts">
  import { toggle } from "@otto-ec/global-resources/toggle";
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import type { Props, Events } from "./FocusedDialogV1.types.js";
  import { useSlots } from "../../../common/utils/useSlots.svelte.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { useStuckState } from "./lib/useStuckState.svelte.js";
  import { useInAppDetector } from "./lib/useInAppDetector.js";
  import { useUrlParameter } from "./lib/useUrlParameter.svelte.js";
  import { useUrlElementsUpdater } from "./lib/useUrlElementsUpdater.svelte.js";

  const toggleName = "oc_focused_dialog_v1_hide_prev_in_app";

  let {
    closeUrl,
    prevUrl,
    headline = "",
    inApp = false,
    headlineLevel = 1,
    showBackButton = false,
  }: Props = $props();

  const headlineElement = $derived(headlineLevel ? `h${headlineLevel}` : "div");

  const closeUrlGuarded = useUrlParameter(
    "close",
    () => closeUrl,
    (c) => (closeUrl = c),
  );
  const prevUrlGuarded = useUrlParameter(
    "prev",
    () => prevUrl,
    (p) => (prevUrl = p),
  );

  const Host = $host<HTMLOcFocusedDialogV1Element>();
  const slots = useSlots(Host);
  const dispatch = useEventDispatcher<Events>(Host);

  const stuckState = useStuckState();

  let stickyTitleInApp = $state(false);
  useInAppDetector(
    (a) => (inApp = a),
    (s) => (stickyTitleInApp = s),
  );

  useUrlElementsUpdater(Host, prevUrlGuarded, closeUrlGuarded);

  const handleClose = (): void => {
    /*                                                                                 */
    const destination = closeUrlGuarded.value!;

    dispatch("oc-focused-dialog-close", { destination }, { alias: ["oc-close"] });

    /*                                      */
    location.assign(destination);
  };

  const handlePrev = (): void => history.back();
</script>

<div
  class="focused-dialog"
  class:focused-dialog--inApp={inApp}
  class:focused-dialog--sticky-title={stickyTitleInApp}
>
  <div
    bind:this={stuckState.title.element}
    class="title"
    class:title--stuck={stuckState.title.stuck}
  >
    <div
      class="title__prev"
      class:title__prev--hidden={inApp && toggle.get(toggleName, false) && !stickyTitleInApp}
    >
      {#if showBackButton || prevUrlGuarded.value}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <oc-icon-button-v3 oc-aria-label="zurück" icon="arrow-left" onclick={handlePrev}
        ></oc-icon-button-v3>
      {/if}
    </div>

    <slot name="headline">
      <svelte:element this={headlineElement} class="title__headline">{headline}</svelte:element>
    </slot>

    <div class="title__close">
      {#if closeUrlGuarded.value}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <oc-icon-button-v3 icon="close" onclick={handleClose} oc-aria-label="schließen">
        </oc-icon-button-v3>
      {/if}
    </div>
  </div>

  <section class="content-area">
    <slot />
  </section>
  {#if slots.actions}
    <div
      class="actions"
      bind:this={stuckState.actions.element}
      class:actions--stuck={stuckState.actions.stuck}
    >
      <slot name="actions" />
    </div>
  {/if}
</div>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins" as mixins;
  :global {
    :host {
      --content-background-color: initial;
      --content-padding: initial;
      --component-background-color: var(--oc-semantic-color-canvas-background);
    }

    .focused-dialog {
      background-color: var(
        --component-background-color,
        tokens.$oc-component-focused-dialog-background-color
      );

      height: 100%;

      .title {
        display: flex;
        position: static;
        z-index: 2; /*                                                                               */
        top: -2px;
        justify-content: space-between;
        background-color: tokens.$oc-component-focused-dialog-header-background-color;
        padding: 24px 16px 16px;

        &__prev,
        &__close {
          /**
 *
 *
 */
          @include mixins.breakpoint-from-m($to: false, $containerFirst: false) {
            width: tokens.$oc-base-dimension-relative-32; /*                           */
          }
        }

        /**
 *
 */

        &__prev--hidden {
          @include mixins.visually-hidden;
        }

        &__headline {
          text-align: left;
          flex-grow: 1;
          margin: 0;
          padding-top: tokens.$oc-component-focused-dialog-header-title-spacing-top;
          color: tokens.$oc-component-focused-dialog-header-title-color;
          word-break: break-word;
          font: tokens.$oc-component-focused-dialog-header-title-font;
          @include mixins.breakpoint-from-m($to: false, $containerFirst: false) {
            text-align: center;
          }
        }
      }

      /**
 *
 */
      &:not(.focused-dialog--inApp),
      &.focused-dialog--sticky-title {
        .title {
          position: sticky;
          border-bottom: tokens.$oc-component-focused-dialog-header-conditional-border-bottom-width
            solid transparent;
          top: -2px;

          &--stuck {
            border-color: tokens.$oc-component-focused-dialog-header-conditional-border-bottom-color;
          }
        }
      }

      .content-area {
        padding: var(--content-padding, 16px);
        background-color: var(
          --content-background-color,
          tokens.$oc-component-focused-dialog-content-background-color
        );
      }

      .actions {
        background-color: tokens.$oc-component-focused-dialog-action-bar-background-color;
        padding: tokens.$oc-component-focused-dialog-action-bar-spacing-top
          tokens.$oc-component-focused-dialog-action-bar-spacing-x
          tokens.$oc-component-focused-dialog-action-bar-spacing-bottom
          tokens.$oc-component-focused-dialog-action-bar-spacing-x;
        bottom: -1px;
        position: sticky;
        z-index: 1;

        &--stuck {
          border-top: tokens.$oc-component-focused-dialog-action-bar-conditional-border-top-width
            solid tokens.$oc-component-focused-dialog-action-bar-conditional-border-top-color;
        }
      }
    }
  }
</style>
